import React, { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";

// Tenant-controlled Sales Order category sequence (Sep 2026).
//
// Until now the customer Sales Order listed its category sections
// alphabetically. That is deterministic but it is not a menu: a
// restaurant wants Dosas and Idli at the top and Catering at the
// bottom, and the only way to get that was to rename categories until
// the alphabet agreed. This panel lets the tenant say the order
// outright, with no engineering involvement.
//
// Deliberately NOT a category-management subsystem. It cannot create,
// rename, merge or delete a category — those still come from the
// products themselves. It only answers one question: in what order do
// these appear on the Sales Order?
//
// Scope: the sequence is stored on THIS catalog's own document and
// every call below is the tenant- and catalog-scoped admin API, so
// reordering Rudras Indian Cuisine cannot touch another tenant, or
// another catalog of the same tenant.
//
// Move-up/move-down rather than drag: the existing admin surfaces have
// no drag-and-drop anywhere, and explicit buttons work on the Samsung
// tablet the operator actually uses, with keyboard and screen-reader
// support for free.
export default function CategoryOrderPanel({ catalog, onChanged }) {
  const [rows, setRows] = useState([]);
  const [configuredCount, setConfiguredCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/retail/catalogs/${catalog.id}/category-order`);
      // The API already returns the categories in the effective order —
      // configured ones first, everything else behind them — so the
      // list the operator sees is exactly what the Sales Order renders.
      setRows(data?.categories || []);
      setConfiguredCount((data?.order || []).length);
      setDirty(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.message || "Could not load this catalog's categories.",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [catalog.id]);

  useEffect(() => { load(); }, [load]);

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    setRows(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Saving goes through the SAME catalog update endpoint every
      // other catalog setting uses — no second write path to keep in
      // step. The server re-validates and de-duplicates the sequence.
      await api.put(`/retail/catalogs/${catalog.id}`, {
        category_order: rows.map((r) => r.name),
      });
      toast.success("Category order saved.");
      setDirty(false);
      setConfiguredCount(rows.length);
      onChanged && onChanged();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.message || "Could not save the category order.",
      );
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      // An empty sequence is a legitimate value meaning "no configured
      // order" — the Sales Order goes back to alphabetical. Nothing is
      // hidden either way.
      await api.put(`/retail/catalogs/${catalog.id}`, { category_order: [] });
      toast.success("Back to alphabetical order.");
      onChanged && onChanged();
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.message || "Could not reset the category order.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-3"
      data-testid="retail-category-order-panel"
    >
      <header className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-slate-900">Category order</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            The order these sections appear in on your Sales Order. Move the ones
            customers should see first to the top. Categories you have not
            positioned stay visible, alphabetically, after the ones you have.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {configuredCount > 0 && (
            <button
              type="button"
              onClick={reset}
              disabled={saving || loading}
              data-testid="retail-category-order-reset"
              className="h-11 px-3 rounded-lg border border-slate-300 text-sm text-slate-700 inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Alphabetical
            </button>
          )}
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving || loading}
            data-testid="retail-category-order-save"
            className="h-11 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save order
          </button>
        </div>
      </header>

      {loading ? (
        <div className="py-6 text-sm text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading categories…
        </div>
      ) : rows.length === 0 ? (
        <p className="py-6 text-sm text-slate-500" data-testid="retail-category-order-empty">
          This catalog has no products yet, so there are no categories to order.
        </p>
      ) : (
        <ol className="space-y-2" data-testid="retail-category-order-list">
          {rows.map((row, index) => (
            <li
              key={row.name}
              data-testid={`retail-category-order-row-${index}`}
              className="flex items-center gap-3 p-2 pl-3 rounded-xl border border-slate-200"
            >
              <span className="w-6 text-xs tabular-nums text-slate-400">{index + 1}</span>
              <span className="min-w-0 flex-1 text-sm font-medium text-slate-800 truncate">
                {row.name}
              </span>
              <span className="text-[11px] text-slate-500 shrink-0">
                {row.count} item{row.count === 1 ? "" : "s"}
              </span>
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label={`Move ${row.name} up`}
                data-testid={`retail-category-order-up-${index}`}
                className="h-11 w-11 grid place-items-center rounded-lg border border-slate-300 text-slate-600 disabled:opacity-30"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === rows.length - 1}
                aria-label={`Move ${row.name} down`}
                data-testid={`retail-category-order-down-${index}`}
                className="h-11 w-11 grid place-items-center rounded-lg border border-slate-300 text-slate-600 disabled:opacity-30"
              >
                <ArrowDown className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
